import React, { useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import ImageGallery from "./ImageGallery";
import ReactGA from "react-ga";
import "./ProjectModal.css";

// Helper to get project anchor ID (matching Projects.js)
const getProjectAnchorId = (title) => {
  const specialMappings = {
    "RAG-O-MATIC": "rag-o-matic",
    "PrayerHelper.faith": "prayerhelper",
    "Duluth.com": "duluth",
    "The Stepper Motor Music Machine": "steppermotormusic"
  };
  
  if (specialMappings[title]) {
    return specialMappings[title];
  }
  
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '')
    .replace(/^the\s*/g, '')
    .trim();
};

const ProjectModal = ({ 
  project, 
  show, 
  onHide, 
  initialImageIndex = 0,
  onNavigateProject = null, // For previous/next navigation
  projectIndex = null, // Current project index in array
  totalProjects = 0 // Total number of projects
}) => {
  const scrollPositionRef = useRef(0);

  // Handle ESC key to close modal and body scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && show) {
        onHide('esc_key');
      }
    };

    if (show) {
      // Save scroll position before opening
      scrollPositionRef.current = window.scrollY;
      
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    } else {
      // When modal closes, restore scrolling immediately
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      // Ensure overflow is restored on cleanup
      if (!show) {
        document.body.style.overflow = "";
      }
    };
  }, [show, onHide]);

  // Prevent any scrolling behavior when modal closes
  const handleHide = (closeMethod = 'close_button') => {
    // Save current scroll position before closing
    scrollPositionRef.current = window.scrollY;
    
    // Restore body overflow IMMEDIATELY so user can scroll
    document.body.style.overflow = "";
    
    onHide(closeMethod);
    
    // Restore scroll position after close
    setTimeout(() => {
      window.scrollTo(0, scrollPositionRef.current);
    }, 10);
  };

  if (!project) return null;

  return (
    <Modal
      show={show}
      onHide={handleHide}
      size="lg"
      centered
      aria-labelledby="project-modal-title"
      restoreFocus={false}
      enforceFocus={false}
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title id="project-modal-title" style={{ fontSize: '1.75rem', fontWeight: '600' }}>{project.title}</Modal.Title>
      </Modal.Header>
      
      {/* Fixed gallery at top - supports mixed images and videos */}
      <div
        style={{
          padding: '1rem 1rem 0 1rem'
        }}
      >
        <ImageGallery
          images={(() => {
            // Build mixed media array from screenshots/images and videos
            const mediaArray = [];
            
            // Process screenshots array - can include images and videos inline
            if (project.screenshots && project.screenshots.length > 0) {
              project.screenshots.forEach(item => {
                if (typeof item === 'string') {
                  // Legacy: string URL = image
                  mediaArray.push({ type: 'image', url: item });
                } else if (item && item.type) {
                  // Already formatted object (image or video)
                  mediaArray.push(item);
                } else {
                  // Fallback: assume it's an image URL
                  mediaArray.push({ type: 'image', url: item });
                }
              });
            } else if (project.img) {
              // No screenshots, use single img
              mediaArray.push({ type: 'image', url: project.img });
            }
            
            // Append videos from videos array (for backwards compatibility)
            // Note: For inline positioning, add videos directly to screenshots array
            if (project.videos && project.videos.length > 0) {
              project.videos.forEach(video => {
                mediaArray.push({
                  type: 'video',
                  url: video.url,
                  videoType: video.type || 'youtube',
                  title: video.title
                });
              });
            }
            
            return mediaArray;
          })()}
          alt={project.alt}
          initialIndex={initialImageIndex}
          projectId={project ? getProjectAnchorId(project.title) : null}
        />
      </div>
      
      {/* Scrollable text content */}
      <Modal.Body 
        style={{ 
          maxHeight: '50vh', 
          overflowY: 'auto', 
          fontSize: '1.05rem', 
          paddingTop: '0.5rem'
        }} 
        className="modal-body-content"
      >
        {/* Basic intro - always show short description first */}
        <p className="mb-3" style={{ lineHeight: '1.6', fontSize: 'inherit' }}>{project.desc}</p>

        {/* Features list - right after intro */}
        {project.features && project.features.length > 0 && (
          <div className="mb-3">
            <h6 className="mb-2" style={{ fontSize: '1.05rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Key Features</h6>
            <ul className="mt-2 mb-0" style={{ lineHeight: '1.7', paddingLeft: '1.25rem', fontSize: 'inherit' }}>
              {project.features.map((feature, featureIndex) => (
                <li key={featureIndex} style={{ marginBottom: '0.4rem' }}>{feature}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Expanded description - after features */}
        {project.expandedDescription && (
          <div className="mb-3">
            <p style={{ lineHeight: '1.6', fontSize: 'inherit' }}>{project.expandedDescription}</p>
          </div>
        )}

        {/* Challenges/Highlights */}
        {project.challenges && (
          <div className="mb-3">
            <h6 className="mb-2" style={{ fontSize: '1.05rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Highlights</h6>
            <p className="mt-2 mb-0" style={{ lineHeight: '1.6', fontSize: 'inherit' }}>{project.challenges}</p>
          </div>
        )}

        {/* Technologies chips - at the bottom of content, above footer buttons */}
        {project.technologies && project.technologies.length > 0 && (
          <div className="mb-0 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
            {project.technologies.map((tech, techIndex) => (
              <span key={techIndex} className="badge bg-secondary me-1 mb-1" style={{ fontSize: '0.9rem', padding: '0.35em 0.65em' }}>
                {tech}
              </span>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between align-items-center">
        <div>
          {/* Previous/Next project navigation */}
          {onNavigateProject && projectIndex !== null && totalProjects > 1 && (
            <div className="d-flex gap-2">
              {projectIndex > 0 && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onNavigateProject(projectIndex - 1)}
                  aria-label="Previous project"
                >
                  ← Previous
                </Button>
              )}
              {projectIndex < totalProjects - 1 && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onNavigateProject(projectIndex + 1)}
                  aria-label="Next project"
                >
                  Next →
                </Button>
              )}
            </div>
          )}
        </div>
        <div className="d-flex gap-2">
          {project.footerURL1 && (
            <Button
              as="a"
              href={project.footerURL1}
              target="_blank"
              rel="noopener noreferrer"
              variant="primary"
              onClick={() => {
                const linkUrl = project.footerURL1 || 'unknown';
                const linkType = linkUrl.includes('http') ? 'external' : 'internal';
                ReactGA.event({
                  category: 'project_modal',
                  action: 'footer_link_clicked',
                  label: `${project.title}|link_text_${project.footerText1}|link_type_${linkType}|link_url_${linkUrl.substring(0, 50)}|click_source_modal_footer`,
                  value: 1
                });
              }}
            >
              {project.footerText1}
            </Button>
          )}
          {project.footerText2 && project.footerURL2 && (
            <Button
              as="a"
              href={project.footerURL2}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline-secondary"
              onClick={() => {
                const linkUrl = project.footerURL2 || 'unknown';
                const linkType = linkUrl.includes('http') ? 'external' : 'internal';
                ReactGA.event({
                  category: 'project_modal',
                  action: 'footer_link_clicked',
                  label: `${project.title}|link_text_${project.footerText2}|link_type_${linkType}|link_url_${linkUrl.substring(0, 50)}|click_source_modal_footer`,
                  value: 1
                });
              }}
            >
              {project.footerText2}
            </Button>
          )}
          <Button variant="secondary" onClick={handleHide}>
            Close
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ProjectModal;

